/**
 * Rinsepay root component.
 *
 * Replaces the old expo-router entry point (app/_layout.tsx) with an
 * explicit react-navigation setup.  The provider tree is preserved
 * verbatim from the old _layout:
 *
 *     Auth → ErrorBoundary → Theme → Stripe → Toast → Navigation
 *
 * Navigation state is owned by <NavigationContainer> and linked to the
 * navigationRef singleton exported from src/navigation/navigationRef.
 * Imperative navigation from outside components (AuthContext, api client
 * on 401, etc.) goes through that ref via the expo-router compat shim.
 *
 * The iOS Dynamic Type font-scale cap and the Android safe-area bottom
 * padding logic are carried over from the old _layout without change.
 */
import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Platform,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  Linking,
} from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';
import { NavigationContainer } from '@react-navigation/native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';

import { navigationRef } from './src/navigation/navigationRef';
import { linking } from './src/navigation/linking';
import { RootNavigator } from './src/navigation/RootNavigator';
import { AuthProvider } from './src/context/AuthContext';
import { NetworkProvider } from './src/context/NetworkContext';
import { ThemeProvider, useTheme } from './src/context/ThemeContext';
import { ActiveSessionProvider } from './src/context/ActiveSessionContext';
import { ToastProvider } from './src/components/Toast';
import { OfflineBanner } from './src/components/OfflineBanner';
import ReturnBanner from './src/components/ReturnBanner';
import { StripeRootProvider } from './src/stripe/StripeProvider';
import UpdateRequiredScreen from './src/components/UpdateRequiredScreen';
import { useVersionGate } from './src/api/versionGate';

// ---------------------------------------------------------------------------
// iOS Dynamic Type cap (carried over from old _layout.tsx)
// Runs once at module load so every Text / TextInput that mounts after this
// point inherits the cap.
// ---------------------------------------------------------------------------
if ((Text as any).defaultProps == null) (Text as any).defaultProps = {};
(Text as any).defaultProps.maxFontSizeMultiplier = 1.2;
if ((TextInput as any).defaultProps == null) (TextInput as any).defaultProps = {};
(TextInput as any).defaultProps.maxFontSizeMultiplier = 1.2;

// ---------------------------------------------------------------------------
// Error boundary — top-level crash recovery UI
// ---------------------------------------------------------------------------
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <View style={ebStyles.container}>
          <Text style={ebStyles.title}>Something went wrong</Text>
          <Text style={ebStyles.subtitle}>
            The app encountered an unexpected error.
          </Text>
          <TouchableOpacity
            style={ebStyles.button}
            onPress={() => this.setState({ hasError: false })}
          >
            <Text style={ebStyles.buttonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

const ebStyles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    backgroundColor: '#fff',
  },
  title: { fontSize: 20, fontWeight: '700', marginBottom: 8, color: '#1a1a1a' },
  subtitle: { fontSize: 14, color: '#666', marginBottom: 24, textAlign: 'center' },
  button: {
    backgroundColor: '#2563eb',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});

// ---------------------------------------------------------------------------
// Themed root — has to live inside <ThemeProvider> to read colors
// ---------------------------------------------------------------------------
const ANDROID_MIN_BOTTOM_PADDING = 48;

function ThemedRoot() {
  const { colors, isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const versionInfo = useVersionGate();
  const [recDismissed, setRecDismissed] = useState(false);
  const bottomPadding =
    Platform.OS === 'android'
      ? Math.max(insets.bottom, ANDROID_MIN_BOTTOM_PADDING)
      : insets.bottom;

  // Hard gate: a too-old client can't reach any screen (fails open while the
  // check is pending or if it errors — we only block on a positive result).
  if (versionInfo?.updateRequired) {
    return (
      <View style={{ flex: 1, paddingBottom: bottomPadding, backgroundColor: colors.background }}>
        <StatusBar barStyle={isDark ? 'light-content' : 'dark-content'} backgroundColor={colors.surface} />
        <UpdateRequiredScreen info={versionInfo} />
      </View>
    );
  }

  const showRecommended = !!versionInfo?.updateRecommended && !recDismissed;

  return (
    <View style={{ flex: 1, paddingBottom: bottomPadding, backgroundColor: colors.background }}>
      <StatusBar
        barStyle={isDark ? 'light-content' : 'dark-content'}
        backgroundColor={colors.surface}
      />
      <OfflineBanner />
      {/* Global "return to your running wash/vacuum" strip — fed by the ONE
          ActiveSessionContext poller; visible on every screen except the
          session's own screen and auth/welcome (see ReturnBanner). */}
      <ReturnBanner />
      {showRecommended && (
        <View style={[updStyles.recBar, { backgroundColor: colors.primaryBg }]}>
          <TouchableOpacity
            style={{ flex: 1 }}
            onPress={() => { if (versionInfo?.storeUrl) Linking.openURL(versionInfo.storeUrl).catch(() => {}); }}
            accessibilityRole="button"
          >
            <Text style={[updStyles.recText, { color: colors.primary }]} numberOfLines={1}>
              A new version of RinsePay is available — tap to update
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setRecDismissed(true)} hitSlop={10} accessibilityLabel="Dismiss">
            <Text style={[updStyles.recDismiss, { color: colors.primary }]}>✕</Text>
          </TouchableOpacity>
        </View>
      )}
      <NavigationContainer
        ref={navigationRef}
        linking={linking}
        documentTitle={{
          formatter: (options, route) => `RinsePay-${options?.title ?? route?.name ?? 'App'}`,
        }}
      >
        <RootNavigator />
      </NavigationContainer>
    </View>
  );
}

const updStyles = StyleSheet.create({
  recBar: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 8 },
  recText: { fontSize: 13, fontWeight: '600' },
  recDismiss: { fontSize: 14, fontWeight: '700', paddingHorizontal: 4 },
});

// ---------------------------------------------------------------------------
// Root App
// ---------------------------------------------------------------------------
export default function App() {
  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <NetworkProvider>
          <AuthProvider>
            <ErrorBoundary>
              <ThemeProvider>
                <StripeRootProvider>
                  <ToastProvider>
                    <ActiveSessionProvider>
                      <ThemedRoot />
                    </ActiveSessionProvider>
                  </ToastProvider>
                </StripeRootProvider>
              </ThemeProvider>
            </ErrorBoundary>
          </AuthProvider>
        </NetworkProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}
